import { useState } from 'react';
import { Banner, BlockStack, Button, Checkbox, Text } from '@shopify/polaris';
import { TextField } from '../forms';
import { useApi } from '../../hooks/useApi';
import type { PriorInstallation, PrivacyRequest } from './types';

type Props = { request: PrivacyRequest; notice: PriorInstallation; disabled: boolean; onChanged: () => Promise<void> };

export default function StoreNoticeReview({ request, notice, disabled, onChanged }: Props) {
  const { fetchApi, loading, error } = useApi();
  const [previous, setPrevious] = useState(false);
  const [external, setExternal] = useState(false);
  const [provided, setProvided] = useState(false);
  const [current, setCurrent] = useState(false);
  const [body, setBody] = useState('');
  const busy = loading || disabled;
  const dataRequest = request.topic === 'customers/data_request';
  const save = async (path: string, fields: Record<string, unknown>) => {
    const result = await fetchApi(`/api/shopify-privacy/${request.id}/${path}`, {
      method: 'POST', body: JSON.stringify({ ...fields, revision: request.revision }),
    });
    if (result) { setBody(''); await onChanged(); }
  };
  return <BlockStack gap="300">
    <Banner tone="warning">{notice.scope === 'previous'
      ? 'This delivery belongs to the earlier store installation. Review its removal before responding.'
      : 'Check this delivery with Shopify to find out which installation it belongs to. Access to current customer records is restricted until then.'}</Banner>
    <Text as="p">Shopify delivery ID: {notice.delivery_id}</Text>
    {error && <Banner tone="critical">{error}</Banner>}
    {request.status === 'held' && <Button disabled={busy} onClick={() => save('review', { action:'resume', reason:'Returning to review the original Shopify installation.' })}>Return to review</Button>}
    {request.status === 'pending_action' && <>
      {notice.prior_removal_status && <Text as="p">Earlier store removal: {notice.prior_removal_status === 'completed' ? 'completed' : 'awaiting cleanup or confirmation'}.</Text>}
      {notice.prior_removal_status === 'completed' && <>
        <Checkbox label="I verified with the store owner or Shopify that this request belongs to the earlier installation." checked={previous} onChange={setPrevious} disabled={busy || current} />
        <Checkbox label={dataRequest ? 'I reviewed all copies held outside Whenly.' : 'I confirmed removal of any remaining copies, including those held by providers.'} checked={external} onChange={setExternal} disabled={busy || current} />
        {dataRequest && <Checkbox label="I gave the store owner the response and any data still held outside Whenly." checked={provided} onChange={setProvided} disabled={busy || current} />}
        <Button disabled={busy || current || !previous || !external || dataRequest && !provided} onClick={() => save('complete',{
          action:'complete_previous_notice', confirm_previous_installation:previous, confirm_external:external, confirm_provided:provided,
        })}>Complete earlier installation request</Button>
      </>}
      {notice.confirmable_install && <>
        <Checkbox label={`I verified with Shopify that this delivery is for the installation started on ${notice.confirmable_install} UTC.`} checked={current}
          onChange={value => { setCurrent(value); if (!value) setBody(''); }} disabled={busy || previous} />
        {current && <>
          <TextField label="Original Shopify delivery body" value={body} onChange={setBody} multiline={4} autoComplete="off"
            helpText="Paste the exact request body from Shopify. Whenly checks it against the signed delivery before opening the usual review." />
          <Button disabled={busy || !body.trim()} onClick={() => save('confirm-installation',{
            confirm_current_installation:true, installed_at:notice.confirmable_install, shopify_body:body,
          })}>Review confirmed installation</Button>
        </>}
      </>}
    </>}
  </BlockStack>;
}
